import { type PhoebeFooterStatus } from '@apps/web/components/phoebe_chat/event_transcript';
import { isRecord } from '@apps/web/components/phoebe_chat/record_utils';
import {
    type LiveRunStatus,
    type ProviderErrorBadgeState,
    type StreamRunNode,
    type StreamRunState,
} from '@apps/web/components/phoebe_chat/streaming_types';
import {
    isActiveRunState,
    isApprovalOnlyRunState,
    isUserInputOnlyRunState,
} from '@apps/web/components/phoebe_chat_view/run_status/run_state_predicates';
import { normalizeDeclaredExecutionPlanTitle } from '@apps/web/components/phoebe_chat_view/tool_calls/infrastructure_tool_ui';
import { isSilentInternalToolCall } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_renderers';
import { formatToolName } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_labels';
import { EXECUTION_PLAN_TOOL_NAME } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_name_constants';
import { lookupToolStepLabel } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_step_labels';
import { type PhoebeAgentRunStateRead } from '@apps/web/hooks/use_phoebe_agent_run_event_stream';

export function deriveLiveStatusFromRun(
    run: StreamRunState | null,
): LiveRunStatus | null {
    if (!run || run.status !== 'running') {
        return null;
    }

    const orderedNodes = run.orderedNodeIds
        .map((nodeId) => run.nodesById[nodeId])
        .filter((node): node is StreamRunNode => node !== undefined);

    for (let index = orderedNodes.length - 1; index >= 0; index -= 1) {
        const node = orderedNodes[index];
        if (!node) {
            continue;
        }

        if (node.kind === 'tool' && !node.isComplete) {
            const toolName = node.toolName ?? null;
            const label = toolName
                ? formatToolName(toolName, false, node.arguments)
                : 'Running tool';
            return {
                phase: 'tool',
                label,
                detail: null,
                toolName,
            };
        }

        if (node.kind === 'activity') {
            return {
                phase: node.phase ?? 'thinking',
                label: node.label ?? 'Thinking',
                detail: null,
                toolName: node.toolName,
            };
        }

        if (node.kind === 'response') {
            if (node.responseKind === 'interstitial') {
                continue;
            }
            return {
                phase: 'thinking',
                label: 'Thought',
                detail: null,
                toolName: null,
            };
        }

        if (node.kind === 'reasoning') {
            return {
                phase: 'thinking',
                label: 'Thinking',
                detail: null,
                toolName: null,
            };
        }
    }

    return {
        phase: 'thinking',
        label: 'Thinking',
        detail: null,
        toolName: null,
    };
}

export function deriveManualLiveStatusFromRunState(
    runState: PhoebeAgentRunStateRead | null,
): LiveRunStatus | null {
    if (
        !runState ||
        runState.activity_type !== 'workflow' ||
        !isActiveRunState(runState) ||
        isApprovalOnlyRunState(runState) ||
        isUserInputOnlyRunState(runState)
    ) {
        return null;
    }

    const header = (
        runState as {
            readonly header?: PhoebeAgentRunStateRead['header'];
        }
    ).header;
    const payload = header?.latest_status_contents;
    if (!isRecord(payload) || payload['type'] !== 'status_update') {
        return null;
    }

    const label =
        typeof payload['label'] === 'string' ? payload['label'].trim() : '';
    if (!label) {
        return null;
    }

    const detail =
        typeof payload['detail'] === 'string' && payload['detail'].trim()
            ? payload['detail'].trim()
            : null;
    const toolName =
        typeof payload['tool_name'] === 'string' && payload['tool_name'].trim()
            ? payload['tool_name'].trim()
            : null;
    const rawPhase = payload['phase'];
    const phase =
        rawPhase === 'thinking' ||
        rawPhase === 'tool' ||
        rawPhase === 'responding'
            ? rawPhase
            : toolName
              ? 'tool'
              : 'thinking';

    return {
        phase,
        label,
        detail,
        toolName,
    };
}

export function derivePhoebeFooterStatus({
    runState,
    manualStatus,
    isMonitoring,
    isSending,
    isAwaitingRunCompletion,
    isStopping,
    isLiveStreamRunning,
    legacyAgentThinking,
    providerErrorBadge,
    hasPendingSubagentWork = false,
}: {
    readonly runState: PhoebeAgentRunStateRead | null;
    readonly manualStatus: LiveRunStatus | null;
    readonly isMonitoring: boolean;
    readonly isSending: boolean;
    readonly isAwaitingRunCompletion: boolean;
    readonly isStopping: boolean;
    readonly isLiveStreamRunning: boolean;
    readonly legacyAgentThinking: boolean;
    readonly providerErrorBadge: ProviderErrorBadgeState | null;
    readonly hasPendingSubagentWork?: boolean;
}): PhoebeFooterStatus {
    if (isStopping) {
        return {
            label: 'Phoebe is stopping',
            tone: 'working',
            showSpinner: true,
        };
    }
    if (runState) {
        if (runState.status === 'failed') {
            if (providerErrorBadge?.kind === 'irrecoverable') {
                return {
                    label: providerErrorBadge.label,
                    detail: providerErrorBadge.detail,
                    tone: 'error',
                    showSpinner: false,
                    canRetryRunError: true,
                };
            }
            return {
                label: 'Phoebe hit an error',
                tone: 'error',
                showSpinner: false,
            };
        }
        if (runState.status === 'cancelled') {
            return {
                label: 'Phoebe stopped',
                tone: 'idle',
                showSpinner: false,
            };
        }
        if (isApprovalOnlyRunState(runState)) {
            return {
                label: 'Phoebe is waiting for approval',
                tone: 'waiting',
                showSpinner: false,
            };
        }
        if (isUserInputOnlyRunState(runState)) {
            return {
                label: 'Phoebe is waiting for your answer',
                tone: 'waiting',
                showSpinner: false,
            };
        }
    }
    if (isMonitoring) {
        return {
            label: 'Phoebe is waiting for responses…',
            tone: 'waiting',
            showSpinner: true,
            canStopMonitoring: true,
        };
    }
    if (manualStatus) {
        return {
            label: manualStatus.label,
            tone: 'working',
            showSpinner: true,
        };
    }
    if (isSending || isAwaitingRunCompletion) {
        return {
            label: 'Phoebe is thinking',
            tone: 'working',
            showSpinner: true,
        };
    }
    if (isLiveStreamRunning) {
        return {
            label: 'Phoebe is thinking',
            tone: 'working',
            showSpinner: true,
        };
    }
    if (runState) {
        if (runState.is_generating) {
            return {
                label: 'Phoebe is thinking',
                tone: 'working',
                showSpinner: true,
            };
        }
        if (
            runState.status === 'running' ||
            runState.has_active_lease ||
            runState.has_pending_work ||
            runState.interrupt_requested
        ) {
            return {
                label: 'Phoebe is working',
                tone: 'working',
                showSpinner: true,
            };
        }
    }
    if (legacyAgentThinking) {
        return {
            label: 'Phoebe is thinking',
            tone: 'working',
            showSpinner: true,
        };
    }
    if (hasPendingSubagentWork) {
        // The run is paused while hidden evaluators finish a recommendation
        // batch — background work, not idle.
        return {
            label: 'Phoebe is reviewing caregivers in the background',
            tone: 'working',
            showSpinner: true,
        };
    }
    return {
        label: 'Phoebe is awaiting instructions',
        tone: 'idle',
        showSpinner: false,
    };
}

export function shouldRenderCompletedRunWrapper(
    run: StreamRunState | null,
): boolean {
    if (!run || run.status === 'running') {
        return false;
    }

    const orderedNodes = run.orderedNodeIds
        .map((nodeId) => run.nodesById[nodeId])
        .filter((node): node is StreamRunNode => node !== undefined);

    const hasReasoningNodes = orderedNodes.some(
        (node) => node.kind === 'reasoning' && node.content.trim().length > 0,
    );
    if (hasReasoningNodes) {
        return true;
    }

    const hasActivityNodes = orderedNodes.some(
        (node) => node.kind === 'activity',
    );
    if (hasActivityNodes) {
        return true;
    }

    const hasToolNodes = orderedNodes.some((node) => node.kind === 'tool');
    if (hasToolNodes) {
        return true;
    }

    const responseNodeCount = orderedNodes.filter(
        (node) => node.kind === 'response' && node.content.trim().length > 0,
    ).length;
    return responseNodeCount > 1;
}

export function isLiveRunTreeDisplayNode(
    node: StreamRunNode,
    runStatus: StreamRunState['status'],
): boolean {
    return (
        node.kind === 'reasoning' ||
        node.kind === 'tool' ||
        (node.kind === 'response' &&
            (runStatus !== 'running' || node.responseKind === 'interstitial'))
    );
}

export function isLiveRunTreeRenderableNode(
    node: StreamRunNode,
    runStatus: StreamRunState['status'],
): boolean {
    if (!isLiveRunTreeDisplayNode(node, runStatus)) {
        return false;
    }

    if (node.kind === 'response') {
        return node.content.trim().length > 0;
    }

    if (node.kind === 'tool') {
        // A silent internal tool call renders nothing, so it must not count
        // as renderable content — otherwise a run whose only node is a
        // hidden feature_gap capture shows an empty toggleable wrapper.
        if (isSilentInternalToolCall(node.toolName, node.arguments)) {
            return false;
        }
        return !(
            (node.status === 'pending_approval' ||
                node.status === 'pending_user_input') &&
            !node.isComplete &&
            node.toolCallId
        );
    }

    return true;
}

export function hasRenderableLiveRunTreeContent(
    run: StreamRunState | null,
): boolean {
    if (!run) {
        return false;
    }

    return run.orderedNodeIds.some((nodeId) => {
        const node = run.nodesById[nodeId];
        return node ? isLiveRunTreeRenderableNode(node, run.status) : false;
    });
}

export function hasVisibleGeneralProgressRunContent(
    run: StreamRunState | null,
): boolean {
    if (!run) {
        return false;
    }

    return run.orderedNodeIds.some((nodeId) => {
        const node = run.nodesById[nodeId];
        if (!node) {
            return false;
        }
        if (node.kind === 'tool') {
            if (isSilentInternalToolCall(node.toolName, node.arguments)) {
                return false;
            }
            if (node.toolName === EXECUTION_PLAN_TOOL_NAME) {
                return hasDeclaredExecutionPlanItems(node);
            }
            return lookupToolStepLabel(node.toolName) !== null;
        }
        return isLiveRunTreeRenderableNode(node, run.status);
    });
}

export function hasDeclaredExecutionPlanItems(node: StreamRunNode): boolean {
    const args = node.arguments;
    if (args === null || typeof args !== 'object' || Array.isArray(args)) {
        return false;
    }
    const rawItems = (args as Record<string, unknown>)['items'];
    if (!Array.isArray(rawItems)) {
        return false;
    }
    return rawItems.some((raw) => {
        if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
            return false;
        }
        const title = (raw as Record<string, unknown>)['title'];
        return (
            typeof title === 'string' &&
            normalizeDeclaredExecutionPlanTitle(title) !== null
        );
    });
}
